import { cn } from "cn";
import type { ComponentProps, ReactNode } from "react";
import { SectionLabel } from "./section-label";

/**
 * Навигация по разделам страницы: подпись над списком пунктов. Оформление
 * только: какой пункт активен и что происходит по нажатию, решает экран.
 * Доступное имя `nav` задаётся `aria-label`.
 */
function SectionNav({
  className,
  label,
  children,
  ...props
}: ComponentProps<"nav"> & {
  /** Подпись над списком. */
  label?: ReactNode;
}) {
  return (
    <nav data-slot="section-nav" className={cn("min-w-0", className)} {...props}>
      {label === undefined ? null : (
        <SectionLabel className="flex h-[22px] items-center px-2 font-medium tracking-[.06em]">
          {label}
        </SectionLabel>
      )}
      <ul className="mt-1 flex flex-col gap-0.5">{children}</ul>
    </nav>
  );
}

type SectionNavItemProps = Omit<ComponentProps<"button">, "children"> & {
  /** Значок слева; декоративен. */
  icon?: ReactNode;
  /** Текущий пункт: получает `aria-current` и заливку. */
  active?: boolean;
  /** Слот справа: точка состояния и тому подобное. */
  indicator?: ReactNode;
  children: ReactNode;
};

function SectionNavItem({
  className,
  icon,
  active = false,
  indicator,
  children,
  ...props
}: SectionNavItemProps) {
  return (
    <li>
      <button
        type="button"
        data-slot="section-nav-item"
        aria-current={active ? "true" : undefined}
        className={cn(
          "flex h-8 w-full items-center gap-2.5 rounded-lg px-2 text-left text-[13px] transition-colors duration-[90ms] ease-linear outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
          active
            ? "bg-accent font-medium text-foreground"
            : "text-muted-foreground hover:text-foreground",
          className,
        )}
        {...props}
      >
        {icon === undefined ? null : (
          <span aria-hidden="true" className="shrink-0 [&>svg]:size-4">
            {icon}
          </span>
        )}
        <span className="min-w-0 flex-1 truncate">{children}</span>
        {indicator}
      </button>
    </li>
  );
}

export type { SectionNavItemProps };
export { SectionNav, SectionNavItem };
